import { Button, CircularProgress, Stack } from '@mui/material';
import Box from '@mui/material/Box';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { FiUserMinus, FiUserPlus } from 'react-icons/fi';
import { toast } from 'react-toastify';
import Axios from '../api/Axios';
import { useAuth } from '../hooks/useAuth';

/**
 * Check In / Check Out widget extracted from SidebarOld.js.
 *
 * Renders nothing unless the `setting_online_attendance` config is enabled and
 * the current user is not role_id "1", matching the original guard.
 */
const CheckInOut = () => {
    const [isCheckable, setIsCheckable] = useState(true);
    const [storeTime, setStoreTime] = useState([]);
    const [shouldFetchData, setShouldFetchData] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    //Context
    const { token, user, isShowCheckInOut, setIsShowCheckInOut } = useAuth();
    const getUserInfo = JSON.parse(localStorage.getItem('user'));

    // Submit Check in/out and Get User Location
    const getUserLocation = () => {
        if (navigator.geolocation) {
            // Loading starts here rather than in the submit handler because the
            // permission prompt / position lookup can itself take a while.
            setIsSubmitting(true);
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;
                    handleAddTimeSubmit();
                },
                (error) => {
                    setIsSubmitting(false);
                    if (error?.code === 1) {
                        toast.error('Your location is blocked. Please enable it');
                    } else {
                        toast.error('Cannot find your location! \n ' + error?.message);
                    }
                }
            );
        } else {
            // display an error if not supported
            toast.error('Geolocation is not supported by this browser.');
        }
    };

    //Attendance time add
    const handleAddTimeSubmit = async () => {
        try {
            const res = await Axios.post(
                `/attendance/create`,
                {
                    employee_id: user?.user_hrm_id,
                    date: moment().format('YYYY-MM-DD'),
                    time: moment().format('hh:mm:ss A'),
                },
                {
                    headers: {
                        Authorization: token
                            ? 'Bearer ' + token
                            : 'Bearer ' + localStorage.getItem('token'),
                    },
                }
            );

            if (res?.data?.status === 'success') {
                toast.success('Time Added');
                setShouldFetchData((prev) => !prev);
            } else if (res?.data?.status === 'error') {
                toast.warn(res?.data?.error[0]);
            }
        } catch (error) {
            toast.warn("Can't add check in/out time");
        } finally {
            setIsSubmitting(false);
        }
    };

    //format check in out
    const formatCheckInOut = () => {
        if (storeTime?.length > 0) {
            if (storeTime?.length % 2 === 0) {
                return storeTime?.toReversed()?.map((item, index) => {
                    if (index % 2 === 0) {
                        return (
                            <Box component="li" key={item?.id ?? index}>
                                Check Out - {moment(item?.time, 'HH:mm:ss').format('hh:mm A')}
                            </Box>
                        );
                    } else {
                        return (
                            <Box component="li" key={item?.id ?? index}>
                                Check In - {moment(item?.time, 'HH:mm:ss').format('hh:mm A')}
                            </Box>
                        );
                    }
                });
            } else if (storeTime?.length % 2 !== 0) {
                return storeTime?.toReversed()?.map((item, index) => {
                    if (index % 2 !== 0) {
                        return (
                            <Box component="li" key={item?.id ?? index}>
                                Check Out - {moment(item?.time, 'HH:mm:ss').format('hh:mm A')}
                            </Box>
                        );
                    } else {
                        return (
                            <Box component="li" key={item?.id ?? index}>
                                Check In - {moment(item?.time, 'HH:mm:ss').format('hh:mm A')}
                            </Box>
                        );
                    }
                });
            }
        }
    };

    //Get Attendance data
    const getAttendanceViewData = async () => {
        try {
            const res = await Axios.post(
                `/attendance/show/${user?.user_hrm_id || getUserInfo?.user_hrm_id}`,
                {
                    date: moment().format('YYYY-MM-DD'),
                },

                {
                    headers: {
                        Authorization: token
                            ? 'Bearer ' + token
                            : 'Bearer ' + localStorage.getItem('token'),
                    },
                }
            );

            if (res.data.status === 'success') {
                setStoreTime(res?.data?.response_data);
            } else if (res.data.success === '200') {
                toast.info("You don't have attendance details permissions, contact admin");
                setStoreTime([]);
            } else {
                setStoreTime([]);
            }
        } catch (error) {
            setStoreTime([]);
            if (error?.response?.status !== 422) {
                toast.warn('Something went wrong about attendacne time');
            }
        }
    };

    useEffect(() => {
        getAttendanceViewData();
    }, [shouldFetchData]);

    //Get Attendance Config Data
    const getConfigData = async () => {
        try {
            const res = await Axios.post(
                '/all/setting',
                {
                    name: ['setting_online_attendance'],
                },
                {
                    headers: {
                        Authorization: token
                            ? 'Bearer ' + token
                            : 'Bearer ' + localStorage.getItem('token'),
                    },
                }
            );

            if (res?.data?.status === 'success') {
                setIsShowCheckInOut(
                    res?.data?.response_data?.setting_online_attendance === '1' ? true : false
                );
            } else if (res.data.success === '200') {
                toast.info("You don't have attendance config permissions, contact admin");
            } else if (res?.data?.status === 'error') {
                toast.warn(res?.data?.error[0]);
            }
        } catch (error) {
            toast.warn('Something went wrong');
        }
    };

    useEffect(() => {
        let isChecking = true;
        if (isChecking) {
            getConfigData();
        }
        return () => {
            isChecking = false;
        };
    }, [shouldFetchData]);

    //Button check in /out
    useEffect(() => {
        if (storeTime?.length === undefined) {
            setIsCheckable(true);
        } else if (storeTime?.length % 2 === 0) {
            setIsCheckable(true);
        } else {
            setIsCheckable(false);
        }
    }, [storeTime]);

    if (!isShowCheckInOut || user?.role_id === '1') {
        return null;
    }

    return (
        <Box
            sx={{
                border: '1px solid #d7d7d7',
                background: '#dbdbdb',
                borderRadius: 2,
                mx: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
            }}
        >
            <Stack
                direction="row"
                justifyContent={'center'}
                alignItems={'center'}
                spacing={1}
                sx={{ py: 3 }}
            >
                <Button
                    size={'small'}
                    variant="contained"
                    color={isCheckable ? 'info' : 'error'}
                    disabled={isSubmitting}
                    startIcon={
                        isSubmitting ? (
                            <CircularProgress size={12} color="inherit" />
                        ) : isCheckable ? (
                            <FiUserPlus />
                        ) : (
                            <FiUserMinus />
                        )
                    }
                    sx={{
                        fontSize: 10,
                        pt: 1,
                        pb: 1,
                        // MUI dims disabled contained buttons to rgba(0,0,0,0.12),
                        // which disappears against this widget's grey background.
                        '&.Mui-disabled': {
                            bgcolor: isCheckable ? 'info.main' : 'error.main',
                            color: '#fff',
                            opacity: 0.7,
                        },
                    }}
                    onClick={getUserLocation}
                >
                    {isSubmitting
                        ? `Checking ${isCheckable ? 'In' : 'Out'}...`
                        : `Check ${isCheckable ? 'In' : 'Out'}`}
                </Button>
            </Stack>
            {storeTime?.length > 0 && (
                <Box
                    component="ul"
                    sx={{
                        maxHeight: 65,
                        overflow: 'auto',
                        mt: 0,
                        mb: 2,
                        li: {
                            mb: 1,
                        },
                    }}
                >
                    {formatCheckInOut()}
                </Box>
            )}
        </Box>
    );
};

export default CheckInOut;
